/**
 * 字段权限表单对话框
 */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, InputNumber } from 'antd';
import { message } from '@/utils/antdStatic';
import { createFieldPermission, updateFieldPermission } from '../../api/dataPermission';
import { getRoleList } from '../../api/role';
import type { FieldPermissionRule, FieldPermissionFormData } from '../../api/types';

interface FieldPermissionFormProps {
  visible: boolean;
  permission: FieldPermissionRule | null;
  permissionTypeOptions: { value: string; label: string }[];
  maskTypeOptions: { value: string; label: string }[];
  resourceTypes: { value: string; label: string; fields?: string[] }[];
  onCancel: () => void;
  onSuccess: () => void;
}

const FieldPermissionForm: React.FC<FieldPermissionFormProps> = ({
  visible,
  permission,
  permissionTypeOptions,
  maskTypeOptions,
  resourceTypes,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [roles, setRoles] = useState<{ value: number; label: string }[]>([]);
  const [selectedPermissionType, setSelectedPermissionType] = useState<string>('');
  const [selectedResourceType, setSelectedResourceType] = useState<string>('');
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const isEdit = !!permission;

  // 获取角色列表
  const fetchRoles = async () => {
    try {
      const res = await getRoleList({ current: 1, pageSize: 100 });
      if (res?.data) {
        const roleList = (res.data as any).list ?? (res.data as any).data ?? res.data;
        setRoles((Array.isArray(roleList) ? roleList : []).map((r: any) => ({
          value: r.id,
          label: r.name,
        })));
      }
    } catch (error) {
      console.error('获取角色列表失败', error);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchRoles();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && permission) {
      const resourceType = (permission as any).resourceType ?? permission.resource_type;
      const fieldName = (permission as any).fieldName ?? permission.field_name;
      const maskType = (permission as any).maskType ?? permission.mask_type;
      const maskPattern = (permission as any).maskPattern ?? permission.mask_pattern;
      const roleId = (permission as any).roleId ?? permission.role_id;
      setSelectedPermissionType(permission.permission);
      setSelectedResourceType(resourceType);

      // 设置可用字段
      const resource = resourceTypes.find(r => r.value === resourceType);
      if (resource?.fields) {
        setAvailableFields(resource.fields);
      }

      form.setFieldsValue({
        name: (permission as any).name,
        resource_type: resourceType,
        field_name: fieldName,
        permission: permission.permission,
        mask_type: maskType,
        mask_pattern: maskPattern,
        role_id: roleId,
        priority: (permission as any).priority,
        status: permission.status,
        description: (permission as any).description,
      });
    } else if (visible) {
      setSelectedPermissionType('');
      setSelectedResourceType('');
      setAvailableFields([]);
      form.resetFields();
      form.setFieldsValue({
        status: 1,
        priority: 0,
      });
    }
  }, [visible, permission, form, resourceTypes]);

  const handleResourceTypeChange = (value: string) => {
    setSelectedResourceType(value);
    form.setFieldsValue({ field_name: undefined });

    const resource = resourceTypes.find(r => r.value === value);
    if (resource?.fields) {
      setAvailableFields(resource.fields);
    } else {
      setAvailableFields([]);
    }
  };

  const handlePermissionTypeChange = (value: string) => {
    setSelectedPermissionType(value);
    if (value !== 'masked') {
      form.setFieldsValue({ mask_type: undefined, mask_pattern: undefined });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        name: values.name,
        resource_type: values.resource_type,
        field_name: values.field_name,
        permission: values.permission,
        mask_type: values.permission === 'masked' ? values.mask_type : undefined,
        mask_pattern: values.permission === 'masked' ? values.mask_pattern : undefined,
        role_id: values.role_id,
        priority: values.priority,
        status: values.status,
        description: values.description,
      } as FieldPermissionFormData & { name?: string; description?: string; priority?: number };

      if (isEdit) {
        await updateFieldPermission(permission!.id, data);
        message.success('更新成功');
      } else {
        await createFieldPermission(data);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error) {
      console.error('提交失败', error);
    }
  };

  // 是否需要脱敏配置
  const needMaskConfig = selectedPermissionType === 'masked';

  return (
    <Modal
      title={isEdit ? '编辑字段权限' : '新增字段权限'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 1,
          priority: 0,
        }}
      >
        <Form.Item
          name="name"
          label="规则名称"
          rules={[{ required: true, message: '请输入规则名称' }]}
        >
          <Input placeholder="请输入规则名称" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="resource_type"
          label="资源类型"
          rules={[{ required: true, message: '请选择资源类型' }]}
        >
          <Select
            placeholder="请选择资源类型"
            options={resourceTypes}
            onChange={handleResourceTypeChange}
          />
        </Form.Item>

        <Form.Item
          name="field_name"
          label="字段名"
          rules={[{ required: true, message: '请选择或输入字段名' }]}
        >
          {availableFields.length > 0 ? (
            <Select
              placeholder="请选择字段"
              options={availableFields.map(f => ({ value: f, label: f }))}
              showSearch
            />
          ) : (
            <Input placeholder="请输入字段名" maxLength={100} />
          )}
        </Form.Item>

        <Form.Item
          name="permission"
          label="权限类型"
          rules={[{ required: true, message: '请选择权限类型' }]}
        >
          <Select
            placeholder="请选择权限类型"
            options={permissionTypeOptions}
            onChange={handlePermissionTypeChange}
          />
        </Form.Item>

        {needMaskConfig && (
          <>
            <Form.Item
              name="mask_type"
              label="脱敏类型"
              rules={[{ required: true, message: '请选择脱敏类型' }]}
            >
              <Select
                placeholder="请选择脱敏类型"
                options={maskTypeOptions}
              />
            </Form.Item>

            <Form.Item
              name="mask_pattern"
              label="脱敏模式"
              tooltip="自定义脱敏模式，如：***隐藏***"
            >
              <Input placeholder="请输入自定义脱敏模式（可选）" maxLength={50} />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="role_id"
          label="适用角色"
        >
          <Select
            placeholder="请选择角色（留空表示适用所有角色）"
            options={roles}
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="priority"
          label="优先级"
          tooltip="数值越大优先级越高"
        >
          <InputNumber min={0} max={100} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: '请选择状态' }]}
        >
          <Select placeholder="请选择状态">
            <Select.Option value={1}>启用</Select.Option>
            <Select.Option value={0}>禁用</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label="描述"
        >
          <Input.TextArea
            placeholder="请输入规则描述"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FieldPermissionForm;
